import { Typography } from '@bowtie-ins/bowkit';
import { BulkQuotation, Product, usePostMessage } from '@bowtie-web/common';
import { Box } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import ProductCard from '../ProductCard/ProductCard';
import { useAttributeAndQsContext } from '../../contexts/AttributeAndQsContext';
import { Link } from '@bowtie-web/components';
import { ALLOWED_PARENT_ORIGIN } from '../../constants/index';

const MIN_ITEM_WIDTH = 250;
const DETAILS_ANCHOR_LINK_KEY = 'scroll-to-rider-details-anchor';

type Props = {
  riderDetailAndQuotations: BulkQuotation[];
  size: 'small' | 'large';
  selectedRiders: string[];
  onSelect: (riderCode: string) => void;
  loading: boolean;
  isValid: boolean;
};

const RidersSelectionPanel: FC<Props> = ({
  riderDetailAndQuotations,
  size,
  selectedRiders,
  onSelect,
  loading,
  isValid,
}) => {
  const { postMessage } = usePostMessage(ALLOWED_PARENT_ORIGIN);
  const { shouldShowMedicalRiderHeadline } = useAttributeAndQsContext();

  const handleClick = useCallback(
    (product: Product) => onSelect(product.code),
    [onSelect],
  );
  const checkIsSelected = useCallback(
    (code: string) => selectedRiders.includes(code),
    [selectedRiders],
  );

  const handleRiderDetailsClick = useCallback(() => {
    postMessage(DETAILS_ANCHOR_LINK_KEY, true);
  }, [postMessage]);

  return (
    <Box display="grid" gridTemplateColumns="1fr" gridGap={8}>
      <Typography variant="body-l" fontWeight="bold">
        {shouldShowMedicalRiderHeadline ? (
          <>
            <FormattedMessage id="riders.title.vhis" />
            <Link
              component="button"
              onClick={handleRiderDetailsClick}
              data-ui-component="Link"
              data-ui-variant="Riders Details"
            >
              <FormattedMessage id="riders.details.vhis" />
            </Link>
          </>
        ) : (
          <FormattedMessage id="riders.title" />
        )}
      </Typography>
      <Box
        display="grid"
        gridTemplateColumns={`repeat(auto-fit, minmax(${MIN_ITEM_WIDTH}px, 1fr))`}
        gridGap={16}
      >
        {loading ? (
          <Skeleton
            variant="rect"
            height={size === 'small' ? 80 : 120}
            style={{ borderRadius: 8 }}
          />
        ) : (
          riderDetailAndQuotations.map(({ quotation, product }) => (
            <ProductCard
              key={product.code}
              product={product}
              selected={checkIsSelected(product.code)}
              onClick={handleClick}
              isValid={isValid}
              size={size}
              quotation={quotation}
              variant="rider"
            />
          ))
        )}
      </Box>
    </Box>
  );
};

export default RidersSelectionPanel;
